import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, Sparkles, ChevronDown, User as UserIcon, ShieldCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import LanguageSwitcher from "./LanguageSwitcher";
import { useAuth } from "@/contexts/AuthContext";

// Primary nav: only the essentials sit in the top bar
const primaryLinks = [
  { label: "Services", path: "/services" },
  { label: "Pricing", path: "/pricing" },
  { label: "Plans", path: "/plans" },
  { label: "Estimate", path: "/estimate" },
  { label: "Gallery", path: "/gallery" },
  { label: "About", path: "/about" },
];

// Everything else lives under "More"
const moreLinks = [
  { label: "Fleet & Dealerships", path: "/dealership-fleet" },
  { label: "Reviews", path: "/reviews" },
  { label: "Blog", path: "/blog" },
];

// Full list for mobile
const allLinks = [
  { label: "Home", path: "/" },
  ...primaryLinks,
  ...moreLinks,
];

const Navbar = () => {
  const { user, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      if (y > lastScrollY && y > 100) setHidden(true);
      else setHidden(false);
      setLastScrollY(y);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [lastScrollY]);

  useEffect(() => { setOpen(false); setMoreOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Close "More" dropdown on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isMoreActive = moreLinks.some((l) => l.path === location.pathname);

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled
          ? "glass-strong shadow-2xl shadow-background/50 border-b border-primary/10"
          : "bg-transparent"
      }`}
      initial={{ y: -80 }}
      animate={{ y: hidden && !open ? -80 : 0 }}
      transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
    >
      <div className="container-luxury flex items-center justify-between h-16 lg:h-[76px] px-4 md:px-8">
        {/* Logo */}
        <Link to="/" className="relative group flex items-center gap-2.5 shrink-0">
          <motion.div
            className="w-9 h-9 rounded-lg gold-gradient-bg flex items-center justify-center"
            whileHover={{ rotate: 8, scale: 1.08 }}
            transition={{ duration: 0.3 }}
          >
            <Sparkles size={17} className="text-primary-foreground" />
          </motion.div>
          <span className="font-display text-xl lg:text-[22px] font-bold tracking-[0.18em] gold-gradient-text-animated">
            METROSHINE
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-1">
          {primaryLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-[13px] font-body font-medium tracking-wide transition-colors duration-300 relative px-3 py-2 ${
                location.pathname === link.path
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {link.label}
              {location.pathname === link.path && (
                <motion.div
                  className="absolute -bottom-0.5 left-3 right-3 h-px"
                  style={{ background: "linear-gradient(90deg, hsl(var(--gold-dark)), hsl(var(--gold-light)))" }}
                  layoutId="navbar-indicator"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
            </Link>
          ))}

          {/* More dropdown */}
          <div ref={moreRef} className="relative">
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className={`text-[13px] font-body font-medium tracking-wide transition-colors duration-300 px-3 py-2 inline-flex items-center gap-1 ${
                isMoreActive || moreOpen
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              More
              <ChevronDown size={13} className={`transition-transform duration-300 ${moreOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {moreOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.96 }}
                  transition={{ duration: 0.18, ease: [0.25, 0.4, 0.25, 1] }}
                  className="absolute top-full right-0 mt-3 min-w-[220px] glass-strong border border-primary/10 rounded-xl shadow-2xl shadow-background/60 overflow-hidden"
                >
                  {moreLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMoreOpen(false)}
                      className={`block px-4 py-3 text-[13px] font-medium tracking-wide transition-colors ${
                        location.pathname === link.path
                          ? "text-primary bg-primary/5"
                          : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Right CTA cluster */}
        <div className="hidden lg:flex items-center gap-2.5 shrink-0 ml-2">
          <LanguageSwitcher />
          <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="relative">
            <Link
              to="/estimate"
              className="relative inline-flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold tracking-wide rounded-full border border-primary/50 text-primary bg-primary/5 hover:bg-primary/10 hover:border-primary transition-all duration-300 backdrop-blur-sm overflow-hidden group"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/15 to-primary/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              <Sparkles size={13} className="text-primary relative" />
              <span className="relative">Instant Estimate</span>
            </Link>
            <span className="absolute -top-1.5 -right-1.5 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
            </span>
          </motion.div>
          <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
            <Link
              to="/book"
              className="gold-gradient-bg text-primary-foreground px-5 py-2.5 text-[13px] font-semibold tracking-wide rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-primary/30 inline-flex items-center gap-1.5"
            >
              Book Now <ArrowRight size={13} />
            </Link>
          </motion.div>
          <Link
            to={user ? (isAdmin ? "/admin" : "/dashboard") : "/auth"}
            className="w-10 h-10 rounded-full border border-primary/30 hover:border-primary hover:bg-primary/10 transition-all flex items-center justify-center text-primary"
            title={user ? "My account" : "Sign in"}
          >
            {isAdmin ? <ShieldCheck size={16} /> : <UserIcon size={16} />}
          </Link>
        </div>

        {/* Mobile toggle */}
        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitcher compact />
        <button
          onClick={() => setOpen(!open)}
          className="text-foreground w-11 h-11 flex items-center justify-center rounded-xl hover:bg-card/50 transition-colors"
          aria-label="Toggle menu"
        >
          <AnimatePresence mode="wait">
            {open ? (
              <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                <X size={24} />
              </motion.div>
            ) : (
              <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                <Menu size={24} />
              </motion.div>
            )}
          </AnimatePresence>
        </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="lg:hidden fixed inset-0 top-16 bg-background z-40"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col px-6 py-8 gap-1 h-full overflow-y-auto">
              {allLinks.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04, duration: 0.4, ease: [0.25, 0.4, 0.25, 1] }}
                >
                  <Link
                    to={link.path}
                    onClick={() => setOpen(false)}
                    className={`text-lg font-display font-semibold py-4 transition-all rounded-xl px-4 flex items-center justify-between ${
                      location.pathname === link.path
                        ? "text-primary bg-primary/5"
                        : "text-foreground/80 hover:bg-card/50"
                    }`}
                  >
                    {link.label}
                    <ArrowRight size={16} className="text-muted-foreground" />
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: allLinks.length * 0.04 + 0.1, duration: 0.4 }}
                className="mt-6 space-y-3"
              >
                <Link
                  to="/book"
                  onClick={() => setOpen(false)}
                  className="gold-gradient-bg text-primary-foreground px-6 py-4 text-base font-semibold tracking-wide rounded-full text-center flex items-center justify-center gap-2"
                >
                  Book Now <ArrowRight size={16} />
                </Link>
                <Link
                  to="/estimate"
                  onClick={() => setOpen(false)}
                  className="border border-primary/40 text-foreground px-6 py-4 text-base font-semibold tracking-wide rounded-full text-center block hover:bg-primary/5 transition-colors"
                >
                  Instant Estimate
                </Link>
                <Link
                  to={user ? (isAdmin ? "/admin" : "/dashboard") : "/auth"}
                  onClick={() => setOpen(false)}
                  className="border border-primary/30 text-primary px-6 py-4 text-base font-semibold tracking-wide rounded-full text-center flex items-center justify-center gap-2 hover:bg-primary/5"
                >
                  {isAdmin ? <ShieldCheck size={16} /> : <UserIcon size={16} />}
                  {user ? "My Account" : "Sign In"}
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
